import { forwardRef } from "react";
import type { AnchorHTMLAttributes, ReactNode } from "react";
import { Link as RouterLink } from "react-router";

// Shim mapping `next/link`'s API onto react-router's <Link>. Only used
// by the SPA build — the Next bundle keeps importing the real package.
//
// We support the props the codebase actually uses:
//   href, className, children, target, onClick, aria-*, title, prefetch.
// `prefetch` is accepted and ignored — react-router does prefetch via
// loaders, not via a per-link flag, and we don't use loaders yet.
type NextLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  children?: ReactNode;
  prefetch?: boolean;
  replace?: boolean;
  scroll?: boolean;
};

const NextLink = forwardRef<HTMLAnchorElement, NextLinkProps>(function NextLink(
  props,
  ref,
) {
  // Pluck Next-only props out via a typed Omit so the spread into
  // react-router doesn't carry incompatible types (its <Link prefetch>
  // takes a string union, not boolean). Using a single rest object
  // also avoids the no-unused-vars lint that fires on `_prefetch`/
  // `_scroll`-style underscore destructures under this project's
  // eslint preset.
  const { href, replace, children, prefetch, scroll, ...rest } =
    props as NextLinkProps;
  void prefetch;
  void scroll;
  // External / mailto / tel / hash-only links route to a plain <a>
  // to match Next's behavior (Next's <Link> only does client navigation
  // for in-app paths; everything else falls through to anchor semantics).
  const isExternal = /^(https?:|mailto:|tel:|#)/.test(href);
  if (isExternal) {
    return (
      <a ref={ref} href={href} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <RouterLink ref={ref} to={href} replace={replace} {...rest}>
      {children}
    </RouterLink>
  );
});

export default NextLink;
